import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { classNames } from 'primereact/utils';
import impersonationService from '../services/impersonationService';
import tenantAdminCreditService from '../services/tenantAdminCreditService';
import axiosInstance from '../config/axios.config';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [impersonating, setImpersonating] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({
        visible: false,
        user: null
    });

    // Pagination state
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search state with debounce
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Sorting state
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);

    // Edit User Dialog
    const [editDialog, setEditDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        first_name: '',
        last_name: '',
        mobile_number: ''
    });
    const [editing, setEditing] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Status Toggle
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [statusConfirmDialog, setStatusConfirmDialog] = useState({
        visible: false,
        user: null,
        newStatus: null
    });
    const [confirmingStatusChange, setConfirmingStatusChange] = useState(false);

    const toast = useRef(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setFirst(0); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Load users when pagination, search, or sort changes
    useEffect(() => {
        loadUsers();
    }, [first, rows, debouncedSearch, sortField, sortOrder]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const page = Math.floor(first / rows) + 1;
            const result = await impersonationService.getImpersonatableUsers({
                page,
                per_page: rows,
                search: debouncedSearch,
                sort_field: sortField,
                sort_order: sortOrder === 1 ? 'asc' : 'desc'
            });

            if (result.success) {
                setUsers(result.users);
                if (result.pagination) {
                    setTotalRecords(result.pagination.total);
                }
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to load users',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Failed to load users:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error?.message || 'Failed to load users',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle pagination change
    const onPage = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    // Handle sort change
    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const handleImpersonateClick = (user) => {
        setConfirmDialog({
            visible: true,
            user: user
        });
    };

    const confirmImpersonation = async () => {
        const user = confirmDialog.user;
        if (!user) return;

        setImpersonating(prev => ({ ...prev, [user.id]: true }));
        setConfirmDialog({ visible: false, user: null });

        try {
            const result = await impersonationService.startImpersonation(user.id);

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'User View Started',
                    detail: `Now viewing from ${user.name}'s perspective`,
                    life: 3000
                });

                // Redirect to dashboard to show the user's view
                setTimeout(() => {
                    window.location.href = '/app/dashboard';
                }, 1000);
            }
        } catch (error) {
            console.error('Impersonation failed:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'User View Failed',
                detail: error?.message || 'Failed to start user view',
                life: 5000
            });
        } finally {
            setImpersonating(prev => ({ ...prev, [user.id]: false }));
        }
    };

    const cancelImpersonation = () => {
        setConfirmDialog({ visible: false, user: null });
    };

    // Edit User Functions
    const openEditDialog = (user) => {
        setEditingUser(user);
        setEditFormData({
            name: user.name || '',
            email: user.email || '',
            first_name: user.user_detail?.first_name || '',
            last_name: user.user_detail?.last_name || '',
            mobile_number: user.mobile_number || user.user_detail?.mobile_number || ''
        });
        setFormErrors({});
        setEditDialog(true);
    };

    const validateForm = () => {
        const errors = {};
        if (!editFormData.name.trim()) {
            errors.name = 'Please enter a username';
        }
        if (!editFormData.email.trim()) {
            errors.email = 'Please enter an email address';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email.trim())) {
            errors.email = 'Please enter a valid email (e.g. name@example.com)';
        }
        if (editFormData.mobile_number && !/^[+\d\s-]{10,15}$/.test(editFormData.mobile_number.trim())) {
            errors.mobile_number = 'Please enter a valid mobile number';
        }
        return errors;
    };

    const handleEditSubmit = async () => {
        if (!editingUser) return;

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors({});

        setEditing(true);
        try {
            const response = await tenantAdminCreditService.updateUserDetails(editingUser.id, editFormData);

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'User details updated successfully',
                    life: 3000
                });
                setEditDialog(false);
                setEditingUser(null);
                loadUsers(); // Reload the user list
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to update user details',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Failed to update user details:', error?.message || 'Unknown error');

            // Handle validation errors with specific field messages
            let errorMessage = 'Failed to update user details';
            const fieldErrors = {};
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                if (errors.email) {
                    fieldErrors.email = 'This email is already in use by another user';
                    errorMessage = fieldErrors.email;
                } else if (errors.mobile_number) {
                    fieldErrors.mobile_number = 'This mobile number is already in use by another user';
                    errorMessage = fieldErrors.mobile_number;
                } else {
                    // Get first error message from any field
                    const firstError = Object.values(errors)[0];
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                }
                if (Object.keys(fieldErrors).length > 0) {
                    setFormErrors(fieldErrors);
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setEditing(false);
        }
    };

    // Status Toggle Functions
    const handleStatusToggle = (user, newStatus) => {
        // If trying to disable, show confirmation dialog
        if (!newStatus) {
            setStatusConfirmDialog({
                visible: true,
                user: user,
                newStatus: newStatus
            });
        } else {
            // If enabling, proceed directly
            confirmStatusChange(user, newStatus);
        }
    };

    const confirmStatusChange = async (user, newStatus) => {
        const targetUser = user || statusConfirmDialog.user;
        const targetStatus = newStatus !== undefined ? newStatus : statusConfirmDialog.newStatus;

        if (!targetUser) return;

        setConfirmingStatusChange(true);
        setUpdatingStatus(prev => ({ ...prev, [targetUser.id]: true }));

        try {
            const response = await axiosInstance.patch(`/admin/users/${targetUser.id}/status`, {
                is_active: targetStatus
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: targetStatus ? 'User has been enabled' : 'User has been disabled',
                    life: 3000
                });

                // Update local state
                setUsers(prev => prev.map(u =>
                    u.id === targetUser.id
                        ? { ...u, is_active: targetStatus }
                        : u
                ));
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Failed to update user status',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Failed to update user status:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to update user status',
                life: 5000
            });
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [targetUser.id]: false }));
            setConfirmingStatusChange(false);
            setStatusConfirmDialog({ visible: false, user: null, newStatus: null });
        }
    };

    const cancelStatusChange = () => {
        setStatusConfirmDialog({ visible: false, user: null, newStatus: null });
    };

    // Column templates
    const nameBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <div className="flex flex-column">
                    <span className="font-medium">{rowData.name}</span>
                    {rowData.user_detail && (rowData.user_detail.first_name || rowData.user_detail.last_name) && (
                        <small className="text-500">
                            {rowData.user_detail.first_name} {rowData.user_detail.last_name}
                        </small>
                    )}
                </div>
            </div>
        );
    };

    const emailBodyTemplate = (rowData) => {
        return <span className="text-900">{rowData.email}</span>;
    };

    const mobileBodyTemplate = (rowData) => {
        const mobile = rowData.mobile_number || rowData.user_detail?.mobile_number;
        return mobile ? (
            <span className="text-900">{mobile}</span>
        ) : (
            <span className="text-500">-</span>
        );
    };

    const ROLE_SEVERITY = {
        company_admin: 'warning',
        tenant_admin:  'danger',
        admin:         'danger',
        super_admin:   'danger',
        manager:       'warning',
        company_user:  'info',
        user:          'secondary',
        owner:         'success',
    };
    const ROLE_LABEL = {
        company_admin: 'Company Admin',
        tenant_admin:  'Tenant Admin',
        admin:         'Admin',
        super_admin:   'Super Admin',
        manager:       'Manager',
        company_user:  'Company User',
        user:          'User',
        owner:         'Owner',
    };

    const userTypeBodyTemplate = (rowData) => {
        const roles = rowData.roles_list || [];
        if (roles.length > 0) {
            return (
                <div className="flex flex-wrap gap-1">
                    {roles.map((role) => (
                        <Tag
                            key={role}
                            value={ROLE_LABEL[role] || role}
                            severity={ROLE_SEVERITY[role] || 'secondary'}
                        />
                    ))}
                    {rowData.company_id && (
                        <Tag value="Has Company" severity="info" icon="pi pi-building" className="text-xs" />
                    )}
                </div>
            );
        }
        // Fallback to user_type if no roles assigned
        const severity = rowData.user_type === 'admin' ? 'danger'
            : rowData.user_type === 'owner' ? 'success'
            : 'secondary';
        return (
            <div className="flex flex-wrap gap-1">
                <Tag value={ROLE_LABEL[rowData.user_type] || rowData.user_type || '—'} severity={severity} />
                {rowData.company_id && (
                    <Tag value="Has Company" severity="info" icon="pi pi-building" className="text-xs" />
                )}
            </div>
        );
    };

    const lastLoginBodyTemplate = (rowData) => {
        if (!rowData.last_login_at) {
            return <span className="text-500">Never</span>;
        }

        const date = new Date(rowData.last_login_at);
        return (
            <div className="flex flex-column">
                <span className="text-700 text-sm">{date.toLocaleDateString()}</span>
                <span className="text-500 text-xs">
                    {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const isUpdating = updatingStatus[rowData.id];
        // Default to true if is_active is null/undefined (for existing users)
        const isActive = rowData.is_active ?? true;

        return (
            <div className="flex align-items-center gap-2">
                <InputSwitch
                    checked={isActive}
                    onChange={(e) => handleStatusToggle(rowData, e.value)}
                    disabled={isUpdating}
                    style={{ opacity: isUpdating ? 0.5 : 1 }}
                />
                {isUpdating ? (
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-spin pi-spinner text-primary" />
                        <span className="text-primary text-sm font-medium">Updating...</span>
                    </div>
                ) : (
                    <Tag
                        value={isActive ? 'Active' : 'Disabled'}
                        severity={isActive ? 'success' : 'danger'}
                        className="text-xs"
                    />
                )}
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        const isCurrentlyImpersonating = impersonating[rowData.id];

        return (
            <div className="flex flex-wrap gap-1">
                <Button
                    icon="pi pi-pencil"
                    severity="warning"
                    outlined
                    size="small"
                    onClick={() => openEditDialog(rowData)}
                    tooltip="Edit user details"
                    tooltipOptions={{ position: 'top' }}
                    aria-label={`Edit ${rowData.name}'s details`}
                />
                <Button
                    icon={isCurrentlyImpersonating ? null : 'pi pi-user-edit'}
                    label={isCurrentlyImpersonating ? 'Loading...' : 'View User'}
                    onClick={() => handleImpersonateClick(rowData)}
                    disabled={isCurrentlyImpersonating}
                    loading={isCurrentlyImpersonating}
                    outlined
                    size="small"
                    tooltip="View application from this user's perspective"
                    tooltipOptions={{ position: 'left' }}
                    aria-label={`View application as ${rowData.name}`}
                />
            </div>
        );
    };

    // Clear search
    const handleClearSearch = () => setSearchQuery('');

    const confirmDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={cancelImpersonation}
                severity="secondary"
                outlined
                size="small"
            />
            <Button
                label="Start User View"
                icon="pi pi-check"
                onClick={confirmImpersonation}
                severity="warning"
                size="small"
                autoFocus
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <Card>
                {/* Filter bar — 5-slot grid: 1 filter + 3 spacers + action block (P7 golden rule) */}
                <div className="surface-ground p-3 border-round mb-3">
                    <div className="grid">
                        <div className="col-12 md:col">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by name, email, mobile..."
                                    aria-label="Search users"
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="col-12 md:col flex gap-2">
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                outlined
                                size="small"
                                onClick={handleClearSearch}
                                disabled={!searchQuery}
                                aria-label="Clear search"
                                className="flex-1 py-1 md:py-2"
                            />
                            <Button
                                icon="pi pi-refresh"
                                label="Refresh"
                                severity="info"
                                outlined
                                size="small"
                                onClick={loadUsers}
                                tooltip="Refresh user list"
                                tooltipOptions={{ position: 'top' }}
                                aria-label="Refresh user list"
                                className="flex-1 py-1 md:py-2"
                            />
                        </div>
                    </div>
                </div>

                <DataTable
                    value={users}
                    lazy
                    paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPage={onPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                    currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                    paginatorClassName="text-sm"
                    pageLinkSize={3}
                    dataKey="id"
                    loading={loading}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    className="p-datatable-gridlines"
                    responsiveLayout="scroll"
                    scrollable
                    scrollHeight="flex"
                    stripedRows
                    size="small"
                    showGridlines
                    emptyMessage={
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-users text-4xl text-400 mb-3" />
                            <span className="text-600">No users found.</span>
                        </div>
                    }
                >
                    <Column
                        field="name"
                        header="Name"
                        body={nameBodyTemplate}
                        sortable
                        frozen
                        alignFrozen="left"
                        style={{ minWidth: '200px' }}

/>
                    <Column
                        field="email"
                        header="Email"
                        body={emailBodyTemplate}
                        sortable
                        style={{ minWidth: '220px' }}
                    />
                    <Column
                        field="mobile_number"
                        header="Mobile"
                        body={mobileBodyTemplate}
                        style={{ minWidth: '130px' }}
                    />
                    <Column
                        field="user_type"
                        header="Role / Type"
                        body={userTypeBodyTemplate}
                        sortable
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="last_login_at"
                        header="Last Login"
                        body={lastLoginBodyTemplate}
                        sortable
                        style={{ minWidth: '130px' }}
                    />
                    <Column
                        field="is_active"
                        header="Status"
                        body={statusBodyTemplate}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        header="Actions"
                        body={actionBodyTemplate}
                        exportable={false}
                        style={{ minWidth: '180px' }}
                        alignHeader="center"
                        bodyClassName="text-center"
                    />
                </DataTable>
            </Card>

            {/* Confirm Impersonation Dialog */}
            <Dialog
                visible={confirmDialog.visible}
                onHide={cancelImpersonation}
                header="Confirm User View"
                footer={confirmDialogFooter}
                style={{ width: '90vw', maxWidth: '450px' }}
                modal
                draggable={false}
            >
                <div className="flex align-items-start gap-3">
                    <i className="pi pi-exclamation-triangle text-orange-500 pi-2x" aria-hidden="true" />
                    <div className="flex flex-column gap-2">
                        <span>
                            Are you sure you want to view the application as{' '}
                            <strong>{confirmDialog.user?.name}</strong>?
                        </span>
                        <small className="text-600">
                            You will see the application exactly as this user sees it.
                        </small>
                    </div>
                </div>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog
                visible={editDialog}
                onHide={() => {
                    if (editing) return;
                    setEditDialog(false);
                    setEditingUser(null);
                    setFormErrors({});
                }}
                header="Edit User Details"
                modal
                draggable={false}
                closable={!editing}
                style={{ width: '90vw', maxWidth: '600px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => {
                                setEditDialog(false);
                                setEditingUser(null);
                                setFormErrors({});
                            }}
                            severity="secondary"
                            outlined
                            size="small"
                            disabled={editing}
                        />
                        <Button
                            label="Save Changes"
                            icon="pi pi-check"
                            onClick={handleEditSubmit}
                            loading={editing}
                            disabled={editing}
                            size="small"
                        />
                    </div>
                }
            >
                <div className="grid formgrid">
                    <div className="field col-12">
                        <label htmlFor="edit-name" className="block font-medium text-sm mb-2">
                            Username <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="edit-name"
                            value={editFormData.name}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter username"
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.name })}
                            aria-describedby="edit-name-help"
                            aria-invalid={!!formErrors.name}
                        />
                        {formErrors.name && (
                            <small id="edit-name-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.name}
                            </small>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="edit-email" className="block font-medium text-sm mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="edit-email"
                            value={editFormData.email}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Enter email"
                            type="email"
                            inputMode="email"
                            autoCapitalize="none"
                            spellCheck={false}
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.email })}
                            aria-describedby="edit-email-help"
                            aria-invalid={!!formErrors.email}
                        />
                        {formErrors.email && (
                            <small id="edit-email-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.email}
                            </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="edit-first-name" className="block font-medium text-sm mb-2">
                            First Name
                        </label>
                        <InputText
                            id="edit-first-name"
                            value={editFormData.first_name}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, first_name: e.target.value }))}
                            placeholder="Enter first name"
                            disabled={editing}
                            className="w-full p-inputtext-sm"
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="edit-last-name" className="block font-medium text-sm mb-2">
                            Last Name
                        </label>
                        <InputText
                            id="edit-last-name"
                            value={editFormData.last_name}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, last_name: e.target.value }))}
                            placeholder="Enter last name"
                            disabled={editing}
                            className="w-full p-inputtext-sm"
                        />
                    </div>

                    <div className="field col-12">
                        <label htmlFor="edit-mobile" className="block font-medium text-sm mb-2">
                            Mobile Number
                        </label>
                        <InputText
                            id="edit-mobile"
                            value={editFormData.mobile_number}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, mobile_number: e.target.value }))}
                            placeholder="Enter mobile number"
                            inputMode="tel"
                            disabled={editing}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': formErrors.mobile_number })}
                            aria-describedby="edit-mobile-help"
                            aria-invalid={!!formErrors.mobile_number}
                        />
                        {formErrors.mobile_number && (
                            <small id="edit-mobile-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" />
                                {formErrors.mobile_number}
                            </small>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* Disable User Confirmation Dialog */}
            <Dialog
                visible={statusConfirmDialog.visible}
                onHide={() => !confirmingStatusChange && cancelStatusChange()}
                header="Confirm Disable User"
                style={{ width: '90vw', maxWidth: '450px' }}
                modal
                draggable={false}
                closable={!confirmingStatusChange}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={cancelStatusChange}
                            severity="secondary"
                            outlined
                            size="small"
                            disabled={confirmingStatusChange}
                        />
                        <Button
                            label={confirmingStatusChange ? 'Disabling...' : 'Disable User'}
                            icon={confirmingStatusChange ? 'pi pi-spin pi-spinner' : 'pi pi-ban'}
                            onClick={() => confirmStatusChange()}
                            severity="danger"
                            size="small"
                            loading={confirmingStatusChange}
                            disabled={confirmingStatusChange}
                            autoFocus
                        />
                    </div>
                }
            >
                <div className="flex align-items-start gap-3">
                    <i className="pi pi-exclamation-triangle text-red-500 pi-2x" aria-hidden="true" />
                    <div className="flex flex-column gap-2">
                        <p className="m-0">
                            Are you sure you want to disable{' '}
                            <strong>{statusConfirmDialog.user?.name}</strong>?
                        </p>
                        <small className="text-600">
                            This user will be logged out immediately and won't be able to login until re-enabled.
                        </small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default UserManagement;
